import { useState, useEffect } from "react";
import {
    db,
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    onSnapshot,
    where
} from "../firebase"; // Ajusta la ruta según tu config
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ClientsScreen() {
    // Estado para la lista de clientes (la que se muestra)
    const [clients, setClients] = useState([]);
    // Estado para guardar la lista original que se va cargando (paginación)
    const [initialClients, setInitialClients] = useState([]);
    // Paginación
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    // Búsqueda
    const [searchTerm, setSearchTerm] = useState("");
    // Cargando
    const [loading, setLoading] = useState(false);

    // Cargar los primeros 30 clientes al montar el componente
    useEffect(() => {
        fetchInitialClients();
    }, []);

    // 1. Trae los primeros 30 clientes (orden descendente por createdAt)
    const fetchInitialClients = async () => {
        try {
            setLoading(true);

            const q = query(
                collection(db, "clients"),
                orderBy("createdAt", "desc"),
                limit(30)
            );

            const snapshot = await (await import("firebase/firestore")).getDocs(q);

            if (!snapshot.empty) {
                const docsArray = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }));

                const lastVisible = snapshot.docs[snapshot.docs.length - 1];

                setClients(docsArray);
                setInitialClients(docsArray); // Guardamos esta lista en initialClients
                setLastDoc(lastVisible);

                if (snapshot.size < 30) {
                    setHasMore(false);
                }
            } else {
                // No hay ningún cliente
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error al cargar clientes:", error);
            toast.error("Error al cargar clientes");
        } finally {
            setLoading(false);
        }
    };

    // 2. Cargar más clientes (paginación)
    const fetchMoreClients = async () => {
        if (!lastDoc) return;

        try {
            setLoading(true);
            const q = query(
                collection(db, "clients"),
                orderBy("createdAt", "desc"),
                startAfter(lastDoc),
                limit(30)
            );

            const snapshot = await (await import("firebase/firestore")).getDocs(q);

            if (!snapshot.empty) {
                const docsArray = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setClients((prev) => [...prev, ...docsArray]);
                setInitialClients((prev) => [...prev, ...docsArray]); // También actualizamos la lista original

                const lastVisible = snapshot.docs[snapshot.docs.length - 1];
                setLastDoc(lastVisible);

                if (snapshot.size < 30) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error al cargar más clientes:", error);
            toast.error("Error al cargar más clientes");
        } finally {
            setLoading(false);
        }
    };

    // 3. Filtrado local (sobre 'clients') según el searchTerm que se escribe en el input
    //    Esto ocurre "en vivo" conforme el usuario escribe (onChange).
    const filteredClients = clients.filter((client) => {
        // Convertimos a minúsculas para comparar case-insensitive
        const firstName = client.firstName?.toLowerCase() || "";
        const razonSocial = client.razonSocial?.toLowerCase() || "";
        const cuit = client.cuit?.toLowerCase() || "";
        const cellPhone = client.cellPhone?.toLowerCase() || "";
        const phone = client.phone?.toLowerCase() || "";
        const email = client.email?.toLowerCase() || "";
        const codeClient = client.codeClient?.toLowerCase() || "";
        const province = client.province?.toLowerCase() || "";
        const type = client.type?.toLowerCase() || "";

        const search = searchTerm.toLowerCase();

        // Si cualquier campo incluye el término de búsqueda, lo mostramos
        return (
            firstName.includes(search) ||
            razonSocial.includes(search) ||
            cuit.includes(search) ||
            cellPhone.includes(search) ||
            phone.includes(search) ||
            email.includes(search) ||
            codeClient.includes(search) ||
            province.includes(search) ||
            type.includes(search)
        );
    });

    // 4. Búsqueda en la base de datos al hacer clic en "Buscar"
    //    (ejemplo: búsqueda parcial por razonSocial).
    //    Ajusta la lógica según el campo que quieras usar o crea varias queries si lo requieres.
    const handleDatabaseSearch = async () => {
        try {
            setLoading(true);

            // Si no hay término de búsqueda, no hacemos la query
            if (!searchTerm.trim()) {
                toast.info("Por favor ingresa un término de búsqueda.");
                return;
            }

            // Ejemplo de búsqueda parcial por razonSocial
            // Utiliza orderBy("razonSocial") para poder hacer la condición >= y <=
            // Ojo: esto implica que necesites un índice en Firestore si usas 'razonSocial'.
            const q = query(
                collection(db, "clients"),
                where("razonSocial", "==", searchTerm),
                limit(30) // opcional, si quieres limitar los resultados
            );


            const snapshot = await (await import("firebase/firestore")).getDocs(q);

            if (!snapshot.empty) {
                const docsArray = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setClients(docsArray);
                // No tocamos initialClients porque ahora estamos mostrando resultados de búsqueda
                // y no queremos pisar la lista original con esto.
                setHasMore(false); // Asumimos que no hay paginación en el "resultado de búsqueda"
            } else {
                setClients([]);
                setHasMore(false);
                toast.info("No se encontraron resultados para la búsqueda.");
            }
        } catch (error) {
            console.error("Error en la búsqueda:", error);
            toast.error("Error al buscar en la base de datos");
        } finally {
            setLoading(false);
        }
    };

    // 5. Limpiar búsqueda y volver a mostrar lo cargado inicialmente (con paginación)
    const handleClearSearch = () => {
        setClients(initialClients); // Restauramos la lista original
        setHasMore(true);          // Habilitamos de nuevo la posibilidad de "Cargar más"
        setSearchTerm("");
    };

    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Listado de Clientes</h1>

            {/* Campo de búsqueda + Botones de acción */}
            <div className="form-control mb-4">
                <label className="label">
                    <span className="label-text font-bold">Buscar Cliente</span>
                </label>
                <div className="flex items-center space-x-2">
                    <input
                        type="text"
                        placeholder="Ingresa nombre, razón social, etc."
                        className="input input-bordered w-full"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button
                        className="btn bg-[#e91c24] border-none text-white"
                        onClick={handleDatabaseSearch}
                        disabled={loading}
                    >
                        {loading ? "Buscando..." : "Buscar"}
                    </button>
                    <button
                        className="btn bg-gray-400 border-none text-white"
                        onClick={handleClearSearch}
                        disabled={loading}
                    >
                        Limpiar
                    </button>
                </div>
            </div>

            {/* Tabla de clientes */}
            <div className="bg-base-100 p-4 rounded shadow">
                <h2 className="text-lg font-bold mb-2">Clientes</h2>
                {filteredClients.length > 0 ? (
                    <table className="table w-full">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Empresa</th>
                                <th>Celular</th>
                                <th>Teléfono</th>
                                <th>Email</th>
                                <th>Cód.Cliente</th>
                                <th>Provincia</th>
                                <th>Categoría</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClients.map((client) => (
                                <tr key={client.id}>
                                    <td>{client.firstName || "--"}</td>
                                    <td>
                                        {client.razonSocial
                                            ? `${client.razonSocial} (${client.cuit || "--"})`
                                            : "--"}
                                    </td>
                                    <td>{client.cellPhone || "--"}</td>
                                    <td>{client.phone || "--"}</td>
                                    <td>{client.mail || "--"}</td>
                                    <td>{client.codeClient || "--"}</td>
                                    <td>{client.province || "--"}</td>
                                    <td>{client.type || "--"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No hay clientes para mostrar.</p>
                )}

                {/* Botón de "Cargar más" centrado */}
                {hasMore && initialClients.length > 0 && (
                    <div className="flex justify-center mt-4">
                        <button
                            className="btn bg-[#e91c24] border-none text-white"
                            onClick={fetchMoreClients}
                            disabled={loading}
                        >
                            {loading ? "Cargando..." : "Cargar más"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
